import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SyntheticEvent } from 'react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import { media } from '@styles/css';

import RoundAvatar from '@components/common/RoundAvatar';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import { IProfileGallery, IProfileGalleryProfile } from '@base/outlookModel';
import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { CurationViewType, useAnalytics } from '@utils/analytics';
import { addDuration, isBefore } from '@utils/date';
import { setFlow } from '@utils/misc';
import { useWebView } from '@utils/webview';

import Badge from '../common/Badge';

const characterCurationCSS = (theme: Theme) => css`
  display: flex-col;
  color: ${theme.var.gray900};

  .listContainer {
    gap: ${Spacing.xxsmall}px;
  }

  h5 {
    ${Typography.h5};
    line-height: 100%;
    margin-bottom: ${Spacing.xhsmall}px;
    padding: 10px 16px;
    ${media.tablet} {
      padding: 10px ${Spacing.xlarge}px;
    }
    ${media.desktop} {
      padding: 10px ${Spacing.xxlarge}px;
    }
  }

  .list-container {
    display: flex;
    padding: 0 ${Spacing.medium}px;
    ${media.tablet} {
      padding: 0 ${Spacing.xlarge}px;
    }
    ${media.desktop} {
      padding: 0 ${Spacing.xxlarge}px;
    }
  }
`;
const listCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;

  min-width: 84px !important;
  align-items: center;
  text-align: center;
  position: relative;
  padding: 10px 0;

  p {
    ${Typography.body14};
    margin-top: ${Spacing.small}px;
    color: ${theme.var.gray800};
  }
  .new-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;

    position: absolute;
    top: 10px;
    right: 16px;
    z-index: 1;

    font-weight: 600;
    line-height: 13.5px;
  }
`;

const truncateStyle = css`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 80px;
`;

export type ProfileGalleryProps = {
  data: IProfileGallery;
};

const ProfileGallery = ({ data }: ProfileGalleryProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { platform, isCheming } = useGlobalContext();
  const { web } = useWebView();
  const { firebase } = useAnalytics();

  const headerTitle = data.title ?? '새로운 캐릭터와 대화해 보세요';

  const handleClickProfile = (
    event: SyntheticEvent,
    profile: IProfileGalleryProfile
  ) => {
    event.preventDefault();
    firebase.logEvent('chatbot_click_play_curation', {
      curation_view_type: 'profile_gallery' as CurationViewType,
    });
    setFlow({ name: headerTitle });
    if (isCheming) {
      if (platform === Platform.web) {
        return router.push(`/play/characters/${profile?.id}`);
      }
      return web.openPage(`/play/characters/${profile?.id}`);
    }

    if (profile.payload.dynamic_object?.chat_room_id) {
      if (platform === Platform.web) {
        router.push(
          `/play/chat-rooms/${profile.payload.dynamic_object?.chat_room_id}`
        );
      }
      web.openPage(
        `/play/chat-rooms/${profile.payload.dynamic_object?.chat_room_id}`
      );
      return;
    }

    if (data.profile_object_type === 'character') {
      if (platform === Platform.web) {
        router.push(`/play/characters/${profile?.id}`);
      }
      web.openPage(`/play/characters/${profile?.id}`);
    }
  };
  return (
    <div css={characterCurationCSS}>
      <h5>{t(headerTitle)}</h5>
      <SwipeableListContainer className={'listContainer'}>
        <div className="list-container">
          {data?.profiles?.map((profile) => {
            const isNewCharacter =
              (profile?.payload?.released_at &&
                isBefore(
                  new Date(),
                  addDuration(new Date(profile?.payload?.released_at), {
                    days: 3,
                  })
                )) ||
              false;
            return (
              <div
                css={listCSS}
                onClick={(e: SyntheticEvent) => handleClickProfile(e, profile)}
                key={profile.id}
              >
                <div>
                  {isNewCharacter && (
                    <Badge
                      text={'N'}
                      radius={Radius.xlarge}
                      backgroundColor={
                        isCheming
                          ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                          : 'linear-gradient(90deg, #04C8D0, #1590D5)'
                      }
                      className={'new-badge'}
                      fontSize={10}
                    />
                  )}
                  <RoundAvatar size={52} picture={profile.image} />
                </div>
                <p css={truncateStyle}>{profile.title}</p>
              </div>
            );
          })}
        </div>
      </SwipeableListContainer>
    </div>
  );
};

export default ProfileGallery;
